/**
 * API client for communicating with the FastAPI backend.
 */

import { API_ENDPOINTS, BACKEND_URL } from '@/config/settings';
import { TIMEOUTS } from '@/constants';
import { clearCsrfToken, getCsrfToken, updateCsrfToken } from '@/utils/helpers';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

export interface ApiError {
  error: string;
  message?: string;
  retry_after?: string;
}

export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  message?: string;
  retry_after?: string;
}

/**
 * Error response structure from Axios/backend
 */
interface AxiosErrorResponse {
  detail?: string;
  message?: string;
}

/**
 * Type guard to check if an error is an AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError<AxiosErrorResponse> {
  return axios.isAxiosError(error);
}

/**
 * API Response Type Definitions
 * These interfaces provide type safety for API responses
 */

export interface UserDataResponse {
  id: string;
  email: string;
  name?: string;
  google_name?: string;
  phone_number?: string;
  phone_verified?: boolean;
  user_group?: {
    id: number;
    name: string;
  };
  is_active?: boolean;
  created_at?: string;
}

export interface RolesResponse {
  user_group?: {
    id: number;
    name: string;
    roles: Array<{
      id: number;
      name: string;
      description?: string;
    }>;
  };
}

export interface TimeWindow {
  start: string;
  end: string | null;
}

export interface AccessPermission {
  device_id: number;
  time_window: TimeWindow;
  data_types: string[];
}

export interface AccessRightsResponse {
  user_id: number;
  permissions: AccessPermission[];
  calculated_at: string | null;
}

export interface RefreshAccessResponse {
  user_id: number;
  permissions: AccessPermission[];
  calculated_at: string;
  message: string;
}

export interface Dataset {
  id: number;
  name?: string;
  device_id?: string;
  data_type_id?: number;
  start_timestamp?: string;
  end_timestamp?: string;
  created_at?: string;
}

export interface DatasetsResponse {
  datasets: Dataset[];
  total: number;
  page: number;
  per_page: number;
}

export interface Order {
  id: number;
  status: string;
  created_at: string;
  phone_number?: string;
  phone_verified?: boolean;
  tos_accepted?: boolean;
  dataset_ids?: number[];
}

export interface OrdersResponse {
  orders: Order[];
  total: number;
  page: number;
  per_page: number;
}

export interface OrderDetailsResponse extends Order {
  datasets?: Dataset[];
  download_links?: Array<{
    dataset_id: number;
    url: string;
    expires_at: string;
  }>;
}

// Paginated response types for server-side search
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  search?: string;
}

export interface DeviceSummary {
  id: number;
  device_id: string;
  name?: string;
  model?: string;
  manufacturer?: string;
  is_active?: boolean;
}

export interface ClinicSummary {
  id: number;
  clinic_id: string;
  name: string;
  location?: string;
  is_active?: boolean;
  device_count: number;
}

export interface StudySummary {
  id: number;
  study_id: string;
  name: string;
  principal_investigator?: string;
  is_active?: boolean;
}

export interface DataTypeSummary {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
}

export interface DatasetSummary {
  id: number;
  name?: string;
  device_id?: number;
  clinic_id?: number;
  study_id?: number;
  data_type_id?: number;
  start_time?: string;
  end_time?: string;
}

export interface RoleSummary {
  id: number;
  name: string;
  description?: string;
}

export interface UserSummary {
  id: number;
  email: string;
  name?: string;
  is_active?: boolean;
}

export interface UserGroupSummary {
  id: number;
  name: string;
  description?: string;
}

class APIClient {
  private client: AxiosInstance;

  constructor(baseUrl: string = BACKEND_URL) {
    this.client = axios.create({
      baseURL: baseUrl,
      timeout: TIMEOUTS.API_DEFAULT,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Required for cookies to work with CORS
    });

    // Note: No Authorization header needed - httponly cookie is sent automatically
    // with withCredentials: true. This is more secure as the token is not accessible to JavaScript.

    // Add request interceptor to fix Mixed Content issues
    // This runs at request time when window.location is definitely available
    this.client.interceptors.request.use(
      (config) => {
        // Fix Mixed Content: If we're on HTTPS but the baseURL is HTTP (non-localhost),
        // convert to relative URL to let the browser use the current origin
        if (
          typeof window !== 'undefined' &&
          window.location.protocol === 'https:' &&
          config.baseURL &&
          config.baseURL.startsWith('http://') &&
          !config.baseURL.includes('localhost')
        ) {
          console.warn(
            '[APIClient] Mixed content prevented: Converting HTTP baseURL to relative URL',
            { originalBaseURL: config.baseURL, url: config.url }
          );
          // Remove the baseURL, making the request relative to current origin
          config.baseURL = '';
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add request interceptor for CSRF protection
    this.client.interceptors.request.use(
      (config) => {
        // Add CSRF token for state-changing requests
        const method = config.method?.toUpperCase();
        if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          const csrfToken = getCsrfToken();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Unauthorized - backend will clear the cookie
          // Avoid redirect loop - don't redirect if already on logout or login page
          const currentPath = window.location.pathname;
          const currentSearch = window.location.search;

          // If already on login page, don't redirect (401 is expected when not authenticated)
          if (currentPath === '/login') {
            // The error is still rejected so the caller can handle it
            // But we don't redirect since user is already on login page
            return Promise.reject(error);
          }

          // If already handling logout, don't redirect
          if (currentSearch.includes('logout=complete')) {
            return Promise.reject(error);
          }

          // If on home page without logout param, redirect to login directly
          if (currentPath === '/') {
            window.location.href = '/login';
            return Promise.reject(error);
          }

          // Otherwise, redirect to logout completion page
          window.location.href = '/?logout=complete';
        }
        return Promise.reject(error);
      }
    );
  }

  private async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'GET',
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      let response;
      switch (method) {
        case 'GET':
          response = await this.client.get<T>(endpoint, config);
          break;
        case 'POST':
          response = await this.client.post<T>(endpoint, data, config);
          break;
        case 'PATCH':
          response = await this.client.patch<T>(endpoint, data, config);
          break;
        case 'PUT':
          response = await this.client.put<T>(endpoint, data, config);
          break;
        case 'DELETE':
          response = await this.client.delete<T>(endpoint, config);
          break;
        default:
          return { error: `Unsupported method: ${method}` };
      }

      return { data: response.data };
    } catch (error) {
      // Check if request was cancelled
      if (axios.isCancel(error)) {
        return { error: 'Request cancelled' };
      }

      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ detail?: string; message?: string }>;

        // Check for aborted request
        if (axiosError.code === 'ERR_CANCELED' || axiosError.message === 'canceled') {
          return { error: 'Request cancelled' };
        }

        if (axiosError.response?.status === 429) {
          const retryAfter = axiosError.response.headers['retry-after'] || '60';
          return {
            error: 'rate_limit_exceeded',
            message: axiosError.response.data?.message || 'Rate limit exceeded',
            retry_after: retryAfter,
          };
        }

        const errorMessage =
          axiosError.response?.data?.detail ||
          axiosError.response?.data?.message ||
          axiosError.message ||
          'Request failed';

        return { error: errorMessage };
      }

      return { error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  }

  // Authentication methods

  /**
   * Fetch a CSRF token from the server and store it for subsequent requests.
   * Should be called once when the user is authenticated (e.g., after login or OAuth).
   */
  async fetchCsrfToken(): Promise<ApiResponse<{ csrf_token: string }>> {
    const response = await this.makeRequest<{ csrf_token: string }>(API_ENDPOINTS.auth_csrf_token);
    if (response.data?.csrf_token) {
      updateCsrfToken(response.data.csrf_token);
    }
    return response;
  }

  async authenticateUser(
    email: string,
    captchaCode: string,
    captchaId: string
  ): Promise<ApiResponse<{ access_token: string; token_type: string }>> {
    return this.makeRequest(API_ENDPOINTS.auth_login, 'POST', {
      email,
      captcha_code: captchaCode,
      captcha_id: captchaId,
    });
  }

  async verifyMagicLink(token: string): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.auth_verify}/${token}`);
  }

  async sendMagicLinkVerificationCode(userId: string): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.auth_verify}/${userId}/send-code`, 'POST');
  }

  async confirmMagicLinkVerificationCode(
    userId: string,
    code: string
  ): Promise<ApiResponse<{ access_token: string; token_type: string }>> {
    return this.makeRequest(`${API_ENDPOINTS.auth_verify}/${userId}/confirm-code`, 'POST', {
      code,
    });
  }

  async getCaptcha(): Promise<ApiResponse<{ image: string; captcha_id: string }>> {
    return this.makeRequest(API_ENDPOINTS.auth_captcha);
  }

  async getGoogleOAuthStatus(): Promise<ApiResponse<{ enabled: boolean }>> {
    return this.makeRequest(API_ENDPOINTS.google_oauth_status);
  }

  async exchangeOAuthToken(oauthCode: string): Promise<ApiResponse<{ access_token: string }>> {
    return this.makeRequest('/api/v1/auth/google/exchange-token', 'POST', {
      oauth_code: oauthCode,
    });
  }

  async logoutUser(): Promise<ApiResponse> {
    const response = await this.makeRequest(API_ENDPOINTS.auth_logout, 'POST');
    // Clear CSRF token on logout
    clearCsrfToken();
    return response;
  }

  // User methods
  async getUserData(): Promise<ApiResponse<UserDataResponse>> {
    return this.makeRequest<UserDataResponse>(API_ENDPOINTS.users_me);
  }

  async getUserRoles(): Promise<ApiResponse<RolesResponse>> {
    return this.makeRequest<RolesResponse>(API_ENDPOINTS.users_roles);
  }

  async updateUserProfile(updateData: Record<string, unknown>): Promise<ApiResponse<UserDataResponse>> {
    return this.makeRequest<UserDataResponse>(API_ENDPOINTS.users_me, 'PATCH', updateData);
  }

  // Access Control methods
  async getMyAccessRights(forceRefresh: boolean = false): Promise<ApiResponse<AccessRightsResponse>> {
    const url = forceRefresh
      ? `${API_ENDPOINTS.access_control_my_rights}?force_refresh=true`
      : API_ENDPOINTS.access_control_my_rights;
    return this.makeRequest<AccessRightsResponse>(url);
  }

  async refreshAccessRights(): Promise<ApiResponse<RefreshAccessResponse>> {
    return this.makeRequest<RefreshAccessResponse>(API_ENDPOINTS.access_control_refresh, 'POST');
  }

  // Dataset methods
  async getDatasets(page: number = 1, perPage: number = 10): Promise<ApiResponse<DatasetsResponse>> {
    return this.makeRequest<DatasetsResponse>(`${API_ENDPOINTS.datasets}/?page=${page}&per_page=${perPage}`);
  }

  async searchDatasets(params: {
    page?: number;
    per_page?: number;
    device_id?: string;
    regex_enabled?: boolean;
    start_timestamp?: string;
    end_timestamp?: string;
    data_types?: string;
  }): Promise<ApiResponse<DatasetsResponse>> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params.device_id) queryParams.append('device_id', params.device_id);
    if (params.regex_enabled) queryParams.append('regex_enabled', 'true');
    if (params.start_timestamp) queryParams.append('start_timestamp', params.start_timestamp);
    if (params.end_timestamp) queryParams.append('end_timestamp', params.end_timestamp);
    if (params.data_types) queryParams.append('data_types', params.data_types);

    return this.makeRequest<DatasetsResponse>(`${API_ENDPOINTS.datasets}/search?${queryParams.toString()}`);
  }

  // Order methods
  async getOrders(params: {
    search?: string;
    status?: string;
    device_id?: string;
    data_type?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<OrdersResponse>> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    return this.makeRequest<OrdersResponse>(
      queryString ? `${API_ENDPOINTS.orders}?${queryString}` : API_ENDPOINTS.orders
    );
  }

  async createOrder(
    datasetIds: number[],
    datasetTimes: Record<string, { start_time: string; end_time: string }>,
    phoneNumber: string,
    tosAccepted: boolean
  ): Promise<ApiResponse<Order>> {
    return this.makeRequest<Order>(API_ENDPOINTS.create_order, 'POST', {
      dataset_ids: datasetIds,
      dataset_times: datasetTimes,
      phone_number: phoneNumber,
      tos_accepted: tosAccepted,
    });
  }

  async getOrderDetails(orderId: number): Promise<ApiResponse<OrderDetailsResponse>> {
    return this.makeRequest<OrderDetailsResponse>(`${API_ENDPOINTS.orders}${orderId}`);
  }

  async getOrderDownloadLinks(orderId: number): Promise<ApiResponse> {
    return this.makeRequest(`/downloads/${orderId}/files`);
  }

  // Phone verification methods
  async requestPhoneVerification(
    orderId: number,
    phoneNumber: string
  ): Promise<ApiResponse> {
    return this.makeRequest(
      API_ENDPOINTS.request_phone_verification.replace('{order_id}', orderId.toString()),
      'POST',
      { phone_number: phoneNumber }
    );
  }

  async confirmPhoneVerification(
    orderId: number,
    code: string,
    phoneNumber: string
  ): Promise<ApiResponse> {
    return this.makeRequest(
      API_ENDPOINTS.confirm_phone_verification.replace('{order_id}', orderId.toString()),
      'POST',
      { code, phone_number: phoneNumber }
    );
  }

  async requestUserPhoneVerification(phoneNumber: string): Promise<ApiResponse> {
    return this.makeRequest(API_ENDPOINTS.user_phone_verification, 'POST', {
      phone_number: phoneNumber,
    });
  }

  async confirmUserPhoneVerification(
    code: string,
    phoneNumber: string
  ): Promise<ApiResponse> {
    return this.makeRequest(API_ENDPOINTS.user_phone_verification_confirm, 'POST', {
      code,
      phone_number: phoneNumber,
    });
  }

  // Bug report methods
  async submitBugReport(
    title: string,
    description: string,
    priority: string = 'medium',
    screenshotData?: string,
    screenshotFilename?: string
  ): Promise<ApiResponse> {
    const payload: Record<string, unknown> = {
      title,
      description,
      priority,
    };
    if (screenshotData) {
      payload.screenshot_data = screenshotData;
      payload.screenshot_filename = screenshotFilename || 'screenshot.png';
    }
    return this.makeRequest(API_ENDPOINTS.bug_report, 'POST', payload);
  }

  // Admin methods
  async getAdminUsers(
    params: {
      skip?: number;
      limit?: number;
      search?: string;
    },
    config?: AxiosRequestConfig
  ): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    return this.makeRequest(
      queryString ? `${API_ENDPOINTS.admin_users}?${queryString}` : API_ENDPOINTS.admin_users,
      'GET',
      undefined,
      config
    );
  }

  async getAdminUser(userId: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_user}/${userId}`);
  }

  async createAdminUser(userData: {
    email: string;
    phone_number?: string;
    user_group_id?: number;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    return this.makeRequest(API_ENDPOINTS.admin_users, 'POST', userData);
  }

  async updateAdminUser(
    userId: number,
    userData: {
      email?: string;
      phone_number?: string;
      user_group_id?: number;
      is_active?: boolean;
      phone_verified?: boolean;
    }
  ): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_user}/${userId}`, 'PUT', userData);
  }

  async deleteAdminUser(userId: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_user}/${userId}`, 'DELETE');
  }

  async getAdminGroups(params: {
    skip?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    return this.makeRequest(
      queryString ? `${API_ENDPOINTS.admin_groups}?${queryString}` : API_ENDPOINTS.admin_groups
    );
  }

  async getAdminGroup(groupId: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_group}/${groupId}`);
  }

  async createAdminGroup(groupData: { name: string; description?: string }): Promise<ApiResponse> {
    return this.makeRequest(API_ENDPOINTS.admin_groups, 'POST', groupData);
  }

  async updateAdminGroup(
    groupId: number,
    groupData: { name: string; description?: string }
  ): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_group}/${groupId}`, 'PUT', groupData);
  }

  async deleteAdminGroup(groupId: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_group}/${groupId}`, 'DELETE');
  }

  async getAdminRoles(): Promise<ApiResponse> {
    return this.makeRequest(API_ENDPOINTS.admin_roles);
  }

  async createAdminRole(roleData: { name: string; description?: string }): Promise<ApiResponse> {
    return this.makeRequest(API_ENDPOINTS.admin_roles, 'POST', roleData);
  }

  async updateAdminRole(
    roleId: number,
    roleData: { name?: string; description?: string }
  ): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_roles}/${roleId}`, 'PUT', roleData);
  }

  async deleteAdminRole(roleId: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_roles}/${roleId}`, 'DELETE');
  }

  async addRoleToGroup(groupId: number, roleId: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_group}/${groupId}/roles/${roleId}`, 'POST');
  }

  async removeRoleFromGroup(groupId: number, roleId: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.admin_group}/${groupId}/roles/${roleId}`, 'DELETE');
  }

  // Dataset management
  async getAdminDatasets(params?: { skip?: number; limit?: number }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const queryString = queryParams.toString();
    return this.makeRequest(`/api/v1/admin/datasets${queryString ? `?${queryString}` : ''}`);
  }

  async createAdminDataset(datasetData: {
    name?: string;
    device_id?: number;
    clinic_id?: number;
    study_id?: number;
    data_type_id: number;
    start_time?: string;
    end_time?: string;
    dataset_metadata?: Record<string, unknown>;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/datasets', 'POST', datasetData);
  }

  async updateAdminDataset(
    datasetId: number,
    datasetData: {
      name?: string;
      device_id?: number;
      clinic_id?: number;
      study_id?: number;
      data_type_id?: number;
      start_time?: string;
      end_time?: string;
      dataset_metadata?: Record<string, unknown>;
    }
  ): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/datasets/${datasetId}`, 'PUT', datasetData);
  }

  async deleteAdminDataset(datasetId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/datasets/${datasetId}`, 'DELETE');
  }

  async bulkCreateAdminDatasets(datasets: Array<{
    name?: string;
    device_id?: number;
    clinic_id?: number;
    study_id?: number;
    data_type_id: number;
    start_time?: string;
    end_time?: string;
    dataset_metadata?: Record<string, unknown>;
  }>): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/datasets/bulk', 'POST', { datasets });
  }

  // Role associations
  async getAdminRoleDetail(roleId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}`);
  }

  async addDeviceToRole(roleId: number, deviceId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/devices/${deviceId}`, 'POST');
  }

  async removeDeviceFromRole(roleId: number, deviceId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/devices/${deviceId}`, 'DELETE');
  }

  async addClinicToRole(roleId: number, clinicId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/clinics/${clinicId}`, 'POST');
  }

  async removeClinicFromRole(roleId: number, clinicId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/clinics/${clinicId}`, 'DELETE');
  }

  async addStudyToRole(roleId: number, studyId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/studies/${studyId}`, 'POST');
  }

  async removeStudyFromRole(roleId: number, studyId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/studies/${studyId}`, 'DELETE');
  }

  async addDataTypeToRole(roleId: number, dataTypeId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/data-types/${dataTypeId}`, 'POST');
  }

  async removeDataTypeFromRole(roleId: number, dataTypeId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/roles/${roleId}/data-types/${dataTypeId}`, 'DELETE');
  }

  // Entity listing
  async getAdminDevices(): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/devices');
  }

  async getAdminClinics(): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/clinics');
  }

  async getAdminStudies(): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/studies');
  }

  async getAdminDataTypes(): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/data-types');
  }

  // Server-side search with pagination for entities (used by ServerSearchableDropdown)
  async searchAdminDevices(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<DeviceSummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<DeviceSummary>>(
      `/api/v1/admin/devices/search?${queryParams.toString()}`
    );
  }

  async searchAdminClinics(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<ClinicSummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<ClinicSummary>>(
      `/api/v1/admin/clinics/search?${queryParams.toString()}`
    );
  }

  async searchAdminStudies(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<StudySummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<StudySummary>>(
      `/api/v1/admin/studies/search?${queryParams.toString()}`
    );
  }

  async searchAdminDataTypes(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<DataTypeSummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<DataTypeSummary>>(
      `/api/v1/admin/data-types/search?${queryParams.toString()}`
    );
  }

  async searchAdminDatasets(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<DatasetSummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<DatasetSummary>>(
      `/api/v1/admin/datasets/search?${queryParams.toString()}`
    );
  }

  async searchAdminRoles(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<RoleSummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<RoleSummary>>(
      `/api/v1/admin/roles/search?${queryParams.toString()}`
    );
  }

  async searchAdminUsers(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<UserSummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<UserSummary>>(
      `/api/v1/admin/users/search?${queryParams.toString()}`
    );
  }

  async searchAdminUserGroups(
    search: string = '',
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<PaginatedResponse<UserGroupSummary>>> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    queryParams.append('page', page.toString());
    queryParams.append('per_page', perPage.toString());
    return this.makeRequest<PaginatedResponse<UserGroupSummary>>(
      `/api/v1/admin/groups/search?${queryParams.toString()}`
    );
  }

  async createAdminDevice(deviceData: {
    device_id: string;
    name?: string;
    model?: string;
    manufacturer?: string;
    description?: string;
    is_active?: boolean;
    clinic_id?: number;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/devices', 'POST', deviceData);
  }

  async updateAdminDevice(deviceId: number, deviceData: {
    device_id?: string;
    name?: string;
    model?: string;
    manufacturer?: string;
    description?: string;
    is_active?: boolean;
    clinic_id?: number;
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/devices/${deviceId}`, 'PUT', deviceData);
  }

  async deleteAdminDevice(deviceId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/devices/${deviceId}`, 'DELETE');
  }

  async createAdminClinic(clinicData: {
    clinic_id: string;
    name: string;
    description?: string;
    location?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/clinics', 'POST', clinicData);
  }

  async updateAdminClinic(clinicId: number, clinicData: {
    clinic_id?: string;
    name?: string;
    description?: string;
    location?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/clinics/${clinicId}`, 'PUT', clinicData);
  }

  async deleteAdminClinic(clinicId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/clinics/${clinicId}`, 'DELETE');
  }

  async createAdminStudy(studyData: {
    study_id: string;
    name: string;
    description?: string;
    principal_investigator?: string;
    start_date?: string;
    end_date?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/studies', 'POST', studyData);
  }

  async updateAdminStudy(studyId: number, studyData: {
    study_id?: string;
    name?: string;
    description?: string;
    principal_investigator?: string;
    start_date?: string;
    end_date?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/studies/${studyId}`, 'PUT', studyData);
  }

  async deleteAdminStudy(studyId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/studies/${studyId}`, 'DELETE');
  }

  async createAdminDataType(dataTypeData: {
    name: string;
    description?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/data-types', 'POST', dataTypeData);
  }

  async updateAdminDataType(dataTypeId: number, dataTypeData: {
    name?: string;
    description?: string;
    is_active?: boolean;
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/data-types/${dataTypeId}`, 'PUT', dataTypeData);
  }

  async deleteAdminDataType(dataTypeId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/data-types/${dataTypeId}`, 'DELETE');
  }

  // CSV Upload for bulk entity creation
  async uploadDataTypesCSV(file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/data-types/csv-upload`,
        formData,
        {
          withCredentials: true,
          headers,
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  async uploadClinicsCSV(file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/clinics/csv-upload`,
        formData,
        {
          withCredentials: true,
          headers,
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  async uploadStudiesCSV(file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/studies/csv-upload`,
        formData,
        {
          withCredentials: true,
          headers,
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  async uploadDevicesCSV(file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/devices/csv-upload`,
        formData,
        {
          withCredentials: true,
          headers,
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  async uploadDatasetsCSV(file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/datasets/csv-upload`,
        formData,
        {
          withCredentials: true,
          headers,
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  // CSV Upload for bulk associations
  async uploadDeviceAssociationsCSV(clinicId: number, file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // For FormData uploads, we need to bypass the default Content-Type header
    // Axios automatically sets multipart/form-data with boundary when it detects FormData
    // Use a direct axios call to avoid the default application/json header
    try {
      // Get CSRF token for the request
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/clinics/${clinicId}/devices/csv-upload`,
        formData,
        {
          withCredentials: true, // Maintain cookie authentication
          headers,
          // Axios will automatically set Content-Type: multipart/form-data with boundary
          // when it detects FormData, so we don't need to set it manually
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  async uploadStudyClinicAssociationsCSV(studyId: number, file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/studies/${studyId}/clinics/csv-upload`,
        formData,
        {
          withCredentials: true,
          headers,
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  async uploadStudyDeviceAssociationsCSV(studyId: number, file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.post(
        `${this.client.defaults.baseURL}/api/v1/admin/studies/${studyId}/devices/csv-upload`,
        formData,
        {
          withCredentials: true,
          headers,
        }
      );
      return { data: response.data };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return { error: error.response?.data?.detail || error.message || 'Upload failed' };
      }
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: 'Upload failed' };
    }
  }

  // Access exploration
  async exploreDeviceAccess(deviceId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/admin/explore-access/device/${deviceId}`);
  }

  // External database sync
  async syncExternalDatabase(entityType?: 'devices' | 'clinics' | 'studies'): Promise<ApiResponse> {
    const params = entityType ? `?entity_type=${entityType}` : '';
    return this.makeRequest(`/api/v1/admin/sync/external-db${params}`, 'POST');
  }

  // External database configuration
  async getExternalDbConfig(): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/sync/external-db/config', 'GET');
  }

  async updateExternalDbConfig(config: {
    enabled?: boolean;
    host?: string;
    port?: number;
    database?: string;
    username?: string;
    password?: string;
    devices_table?: string;
    clinics_table?: string;
    studies_table?: string;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/sync/external-db/config', 'PUT', config);
  }

  async testExternalDbConfig(config: {
    host: string;
    port: number;
    database: string;
    username: string;
    password: string;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/admin/sync/external-db/config/test', 'POST', config);
  }

  // Access Request methods
  async createAccessRequest(
    requestData: {
      requester_email: string;
      department: string;
      requested_data_types: string[];
      data_type_justification: string;
      research_purpose: string;
      irb_approval: string;
      data_retention_period: string;
      access_type: string;
      data_usage_agreement: boolean;
      privacy_policy_agreement: boolean;
      data_security_agreement: boolean;
      specific_devices?: string;
      specific_clinics?: string;
      specific_studies?: string;
      irb_approval_details?: string;
      access_end_date?: string;
      priority?: string;
    },
    file?: File
  ): Promise<ApiResponse<{ success: boolean; message: string; ticket_id?: string }>> {
    if (file) {
      // Use FormData for file upload
      const formData = new FormData();

      // Append all request data fields
      formData.append('requester_email', requestData.requester_email);
      formData.append('department', requestData.department);
      formData.append('requested_data_types', JSON.stringify(requestData.requested_data_types));
      formData.append('data_type_justification', requestData.data_type_justification);
      formData.append('research_purpose', requestData.research_purpose);
      formData.append('irb_approval', requestData.irb_approval);
      formData.append('data_retention_period', requestData.data_retention_period);
      formData.append('access_type', requestData.access_type);
      formData.append('data_usage_agreement', requestData.data_usage_agreement.toString());
      formData.append('privacy_policy_agreement', requestData.privacy_policy_agreement.toString());
      formData.append('data_security_agreement', requestData.data_security_agreement.toString());

      if (requestData.specific_devices) {
        formData.append('specific_devices', requestData.specific_devices);
      }
      if (requestData.specific_clinics) {
        formData.append('specific_clinics', requestData.specific_clinics);
      }
      if (requestData.specific_studies) {
        formData.append('specific_studies', requestData.specific_studies);
      }
      if (requestData.irb_approval_details) {
        formData.append('irb_approval_details', requestData.irb_approval_details);
      }
      if (requestData.access_end_date) {
        formData.append('access_end_date', requestData.access_end_date);
      }
      if (requestData.priority) {
        formData.append('priority', requestData.priority);
      }

      // Append the file
      formData.append('file', file);

      // Don't set Content-Type header - let the browser set it with the correct boundary
      // Use longer timeout for access requests (30s) as they involve Jira API and email sending
      return this.makeRequest('/api/v1/access-requests', 'POST', formData, { timeout: TIMEOUTS.API_ACCESS_REQUEST });
    } else {
      // Use JSON for requests without files
      // Use longer timeout for access requests (30s) as they involve Jira API and email sending
      return this.makeRequest('/api/v1/access-requests', 'POST', requestData, { timeout: TIMEOUTS.API_ACCESS_REQUEST });
    }
  }

  async getMyAccessRequests(): Promise<ApiResponse<{
    success: boolean; requests: Array<{
      id: string;
      key: string;
      summary: string;
      status: string;
      status_category: string;
      created: string;
      updated: string;
      priority?: string;
    }>; error?: string
  }>> {
    return this.makeRequest('/api/v1/access-requests/my-requests');
  }

  async getAccessRequestStatus(ticketId: string): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/access-requests/${ticketId}`);
  }

  // ========================================================================
  // Policy-Based Associations API
  // ========================================================================

  // Clinic-Device Associations
  async getClinicDeviceAssociations(params?: { clinic_id?: number; device_id?: number }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.clinic_id) queryParams.append('clinic_id', params.clinic_id.toString());
    if (params?.device_id) queryParams.append('device_id', params.device_id.toString());
    const query = queryParams.toString();
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/clinic-device${query ? `?${query}` : ''}`);
  }

  async createClinicDeviceAssociation(data: {
    clinic_id: number;
    device_id: number;
    start_timestamp: string;
    end_timestamp: string;
  }): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/clinic-device`, 'POST', data);
  }

  async deleteClinicDeviceAssociation(id: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/clinic-device/${id}`, 'DELETE');
  }

  // Study-Clinic Associations
  async getStudyClinicAssociations(params?: { study_id?: number; clinic_id?: number }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.study_id) queryParams.append('study_id', params.study_id.toString());
    if (params?.clinic_id) queryParams.append('clinic_id', params.clinic_id.toString());
    const query = queryParams.toString();
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/study-clinic${query ? `?${query}` : ''}`);
  }

  async createStudyClinicAssociation(data: {
    study_id: number;
    clinic_id: number;
    start_timestamp: string;
    end_timestamp: string;
  }): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/study-clinic`, 'POST', data);
  }

  async deleteStudyClinicAssociation(id: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/study-clinic/${id}`, 'DELETE');
  }

  // Study-Device Associations
  async getStudyDeviceAssociations(params?: { study_id?: number; device_id?: number }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.study_id) queryParams.append('study_id', params.study_id.toString());
    if (params?.device_id) queryParams.append('device_id', params.device_id.toString());
    const query = queryParams.toString();
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/study-device${query ? `?${query}` : ''}`);
  }

  async createStudyDeviceAssociation(data: {
    study_id: number;
    device_id: number;
    start_timestamp: string;
    end_timestamp: string;
  }): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/study-device`, 'POST', data);
  }

  async deleteStudyDeviceAssociation(id: number): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/study-device/${id}`, 'DELETE');
  }

  // Dataset Associations
  async createDatasetClinicAssociation(data: {
    dataset_id: number;
    clinic_id: number;
    start_timestamp: string;
    end_timestamp: string;
  }): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/dataset-clinic`, 'POST', data);
  }

  async createDatasetStudyAssociation(data: {
    dataset_id: number;
    study_id: number;
    start_timestamp: string;
    end_timestamp: string;
  }): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/dataset-study`, 'POST', data);
  }

  async createDatasetDeviceAssociation(data: {
    dataset_id: number;
    device_id: number;
    start_timestamp: string;
    end_timestamp: string;
  }): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/dataset-device`, 'POST', data);
  }

  // Dataset Policies
  /**
   * Get all policies attached to a dataset
   * @param datasetId - Dataset ID
   * @returns List of policies attached to the dataset
   */
  async getDatasetPolicies(datasetId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/datasets/${datasetId}/policies`);
  }

  /**
   * @deprecated Use getDatasetPolicies instead. This method is kept for backwards compatibility.
   */
  async getDatasetPolicy(datasetId: number): Promise<ApiResponse> {
    return this.getDatasetPolicies(datasetId);
  }

  /**
   * Create a new policy and attach it to a dataset.
   * This is a convenience method that combines createPolicy and attachPolicyToDatasets.
   * @param datasetId - Dataset ID to attach the policy to
   * @param data - Policy data including policy_version and policy_document
   */
  async createDatasetPolicy(datasetId: number, data: {
    policy_version: string;
    policy_document: unknown;
  }): Promise<ApiResponse> {
    // Create the policy first
    const policyName = `Dataset ${datasetId} Policy - ${new Date().toISOString()}`;
    const createResponse = await this.createPolicy({
      name: policyName,
      description: `Auto-created policy for dataset ${datasetId}`,
      policy_document: data.policy_document,
    });

    if (createResponse.error || !createResponse.data) {
      return createResponse;
    }

    // Then attach it to the dataset
    const policyId = (createResponse.data as { id: number }).id;
    return this.attachPolicyToDatasets(policyId, { dataset_ids: [datasetId] });
  }

  /**
   * Remove all policies from a dataset.
   * @param datasetId - Dataset ID to remove policies from
   */
  async deleteDatasetPolicy(datasetId: number): Promise<ApiResponse> {
    // Get all policies for the dataset
    const policiesResponse = await this.getDatasetPolicies(datasetId);
    if (policiesResponse.error || !policiesResponse.data) {
      return policiesResponse;
    }

    // Detach each policy from the dataset
    const policies = policiesResponse.data as Array<{ id: number }>;
    for (const policy of policies) {
      const detachResponse = await this.detachPolicyFromDatasets(policy.id, { dataset_ids: [datasetId] });
      if (detachResponse.error) {
        return detachResponse;
      }
    }

    return { data: { success: true, detached_count: policies.length } };
  }

  async validatePolicy(data: { policy_document: unknown }): Promise<ApiResponse> {
    return this.makeRequest(`${API_ENDPOINTS.ADMIN_ASSOCIATIONS}/policy/validate`, 'POST', data);
  }

  // ============================================================================
  // New Policy Management API (v1/policies endpoints)
  // ============================================================================

  /**
   * List all policies with optional filtering and pagination
   * @param params - Filter and pagination parameters
   */
  async listPolicies(params?: {
    is_template?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    if (params?.is_template !== undefined) {
      queryParams.append('is_template', String(params.is_template));
    }
    if (params?.page !== undefined) {
      queryParams.append('page', String(params.page));
    }
    if (params?.per_page !== undefined) {
      queryParams.append('per_page', String(params.per_page));
    }
    const query = queryParams.toString();
    return this.makeRequest(`/api/v1/policies${query ? `?${query}` : ''}`);
  }

  /**
   * Get a specific policy by ID
   * @param policyId - Policy ID
   */
  async getPolicy(policyId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}`);
  }

  /**
   * Create a new policy
   * @param data - Policy creation data
   */
  async createPolicy(data: {
    name: string;
    description?: string | null;
    policy_document: unknown;
    is_template?: boolean;
    created_by?: number | null;
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/policies', 'POST', data);
  }

  /**
   * Update an existing policy
   * @param policyId - Policy ID
   * @param data - Policy update data
   */
  async updatePolicy(policyId: number, data: {
    name?: string | null;
    description?: string | null;
    policy_document?: unknown | null;
    is_template?: boolean | null;
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}`, 'PUT', data);
  }

  /**
   * Delete a policy
   * @param policyId - Policy ID
   */
  async deletePolicy(policyId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}`, 'DELETE');
  }

  /**
   * Attach a policy to multiple datasets
   * @param policyId - Policy ID
   * @param data - Dataset IDs and priority
   */
  async attachPolicyToDatasets(policyId: number, data: {
    dataset_ids: number[];
    priority?: number;
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}/attach`, 'POST', data);
  }

  /**
   * Detach a policy from multiple datasets
   * @param policyId - Policy ID
   * @param data - Dataset IDs to detach from
   */
  async detachPolicyFromDatasets(policyId: number, data: {
    dataset_ids: number[];
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}/detach`, 'POST', data);
  }

  /**
   * Get all dataset IDs that a policy is attached to
   * @param policyId - Policy ID
   */
  async getPolicyDatasets(policyId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}/datasets`);
  }

  /**
   * Get policy change history
   * @param policyId - Policy ID
   */
  async getPolicyHistory(policyId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}/history`);
  }

  /**
   * Clone a policy template
   * @param policyId - Template policy ID
   * @param data - Clone configuration
   */
  async clonePolicy(policyId: number, data: {
    new_name: string;
    description?: string | null;
    modifications?: Record<string, unknown> | null;
  }): Promise<ApiResponse> {
    return this.makeRequest(`/api/v1/policies/${policyId}/clone`, 'POST', data);
  }

  /**
   * Check if current user can access specific datasets
   * @param data - Dataset IDs to check
   */
  async checkDatasetAccess(data: {
    dataset_ids: number[];
  }): Promise<ApiResponse> {
    return this.makeRequest('/api/v1/policies/check-access', 'POST', data);
  }
}

// Global API client instance
export const apiClient = new APIClient();
